import { mapExchange } from "@urql/core"
import type { Resolver } from "@urql/exchange-graphcache"
import { offlineExchange } from "@urql/exchange-graphcache"
import { makeDefaultStorage } from "@urql/exchange-graphcache/default-storage"
import { refocusExchange } from "@urql/exchange-refocus"
import { retryExchange } from "@urql/exchange-retry"
import type { Client } from "@urql/solid"
import { createClient, fetchExchange } from "@urql/solid"

let _onUserSuspended: (() => void) | null = null

export function registerUserSuspendedHandler(fn: () => void): () => void {
  _onUserSuspended = fn
  return () => {
    _onUserSuspended = null
  }
}

const userSuspendedExchange = mapExchange({
  onResult(result) {
    if (
      result.error?.graphQLErrors?.some(
        e => e.extensions?.code === "USER_SUSPENDED",
      )
    ) {
      _onUserSuspended?.()
    }
    return result
  },
})

import type { GraphCacheConfig } from "@/graphql/generated"
import { introspection } from "@/graphql-env"
import {
  noteMutationUpdaters,
  noteOptimisticUpdaters,
} from "@/lib/urql-cache/notes"
import { reactionsOptimisticUpdaters } from "@/lib/urql-cache/reactions"
import { subscriptionMutationUpdaters } from "@/lib/urql-cache/subscriptions"
import { tagsOptimisticUpdaters } from "@/lib/urql-cache/tags"

export type DeepPartial<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepPartial<U>>
    : { [K in keyof T]?: DeepPartial<T[K]> }
  : T

function createDateTimeResolvers() {
  const resolvers: Record<string, Record<string, Resolver>> = {}

  for (const type of introspection.__schema.types) {
    if (type.kind === "OBJECT" && "fields" in type && type.fields) {
      const dateFields: Record<string, Resolver> = {}

      for (const field of type.fields) {
        const fieldType =
          field.type.kind === "NON_NULL" ? field.type.ofType : field.type

        if (fieldType.kind === "SCALAR" && fieldType.name === "DateTime") {
          dateFields[field.name] = parent => {
            const value = parent[field.name]
            if (typeof value === "string") {
              return new Date(value)
            }
            return value
          }
        }
      }

      if (Object.keys(dateFields).length > 0) {
        resolvers[type.name] = dateFields
      }
    }
  }

  return resolvers
}

const storage = makeDefaultStorage({
  idbName: "fika-graphql-cache-v4",
  maxAge: 7,
})

export function createUrqlClient(): Client {
  const graphqlUrl = "/api/graphql"

  return createClient({
    url: graphqlUrl,
    exchanges: [
      refocusExchange(),
      userSuspendedExchange,
      offlineExchange({
        schema: introspection,
        storage,
        keys: {
          // These are types that are not cached by the default cache exchange
          // as they don't have an identity key
          PostHighlight: () => null,
          PostStatsItem: () => null,
          SubscriberSearchResult: () => null,
          Error: () => null,
          ValidationError: () => null,
          NotFoundError: () => null,
          UnauthorizedError: () => null,
          FlaggedMessageError: () => null,
          UserSuspendedError: () => null,
          PublicationViewStats: () => null,
          TableStatsItem: () => null,
          ReactionSummary: () => null,
          GroupedReaction: () => null,
          ContentAccess: () => null,
        },
        resolvers: {
          ...createDateTimeResolvers(),
        },
        optimistic: {
          ...tagsOptimisticUpdaters,
          ...noteOptimisticUpdaters,
          ...reactionsOptimisticUpdaters,
        },
        updates: {
          Mutation: {
            ...noteMutationUpdaters,
            ...subscriptionMutationUpdaters,
          },
        },
      } satisfies GraphCacheConfig),
      retryExchange(),
      fetchExchange,
    ],
    requestPolicy: "cache-and-network",
    fetchOptions: () => ({
      credentials: "include",
    }),
  })
}
